'use client';

import React from 'react';
import SEOHeading from '@/components/common/SEOHeading';
import Icon from '@/components/ui/AppIcon';
import { trackCTAClick } from '@/lib/seo/conversionTracking';

interface CTASectionProps {
  onScrollToForm: () => void;
}

const CTASection = ({ onScrollToForm }: CTASectionProps) => {
  const handleCTAClick = () => {
    trackCTAClick('cta_section', 'Reserve Your Puppy Today');
    onScrollToForm();
  };

  return (
    <section className="bg-gradient-to-br from-primary via-primary to-secondary py-16 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          {/* Urgency Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 animate-pulse">
            <Icon name="ClockIcon" size={18} variant="solid" className="text-accent-foreground" />
            <span className="text-sm font-semibold text-accent-foreground">
              ⚡ Weekend Special Ends Soon
            </span>
          </div>

          <SEOHeading level={2} className="mb-4 text-primary-foreground">
            Ready to Welcome Your New Best Friend Home?
          </SEOHeading>
          <p className="mb-4 text-lg text-primary-foreground/90">
            Our adorable Labrador puppies are waiting to steal your heart! Whether you've fallen in love with the idea of an English Lab, a sunny Yellow Lab, a sleek Black Lab, a sweet Chocolate Lab, or a rare Fox Red Lab, we have the perfect furry companion ready to fill your home with joy, laughter, and unconditional love. Let's find your perfect match today!
          </p>
          
          {/* Social Proof */}
          <div className="mb-8 flex items-center justify-center gap-2 text-primary-foreground/80">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 w-10 rounded-full border-2 border-primary bg-primary-foreground/20 flex items-center justify-center text-xs font-bold text-primary-foreground">
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <p className="text-sm font-medium">
              <span className="font-bold">47 happy families</span> brought home their dream puppy just this month
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={handleCTAClick}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-accent px-8 font-semibold text-accent-foreground shadow-warm-lg transition-smooth hover:scale-[0.97] hover:shadow-warm-xl focus-ring sm:w-auto"
            >
              Reserve Your Puppy Today
              <Icon name="ArrowRightIcon" size={20} variant="outline" />
            </button>
          </div>

          {/* Risk Reversal */}
          <div className="mt-8 rounded-lg border-2 border-primary-foreground/20 bg-primary-foreground/10 p-6">
            <p className="mb-4 text-lg font-semibold text-primary-foreground">
              🛡️ Your Investment is Completely Protected
            </p>
            <p className="text-sm text-primary-foreground/90">
              If you're not absolutely thrilled with your puppy within the first 30 days, we'll refund every penny - no questions, no hassle. Plus, every single puppy comes with our comprehensive 2-year health guarantee because your peace of mind matters to us.
            </p>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-primary-foreground/80">
            <div className="flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" />
              <span className="text-sm font-medium">2-Year Health Guarantee</span>
            </div>
            <div className="h-6 w-px bg-primary-foreground/30"></div>
            <div className="flex items-center gap-2">
              <Icon name="CurrencyDollarIcon" size={20} variant="solid" />
              <span className="text-sm font-medium">30-Day Money-Back Promise</span>
            </div>
            <div className="h-6 w-px bg-primary-foreground/30"></div>
            <div className="flex items-center gap-2">
              <Icon name="HeartIcon" size={20} variant="solid" />
              <span className="text-sm font-medium">Lifetime Support & Guidance</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;